import React from 'react'
import { FormProvider } from 'react-hook-form'
import styled from 'styled-components'

import { useGetShortlist } from './hooks'

import {
  Message,
  ShortlistFooter,
  ShortlistProperty,
  SignOutButton,
  SuccessfulEmployeeEmail,
  SendToEmployeeModal,
} from '../../components'
import { Spinner } from '../../components/uikit'
import { theme } from '../../theme'

const Container = styled.div`
  max-width: 28rem;
  margin: 0 auto;
  background-color: white;
  min-height: 100vh;

  /* For sticky footer */
  position: relative;
  padding-bottom: 160px;
`

const Header = styled.header`
  padding: 16px 20px 4px 20px;
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
`

const Logo = styled.img`
  width: 2rem;
  height: 2rem;
`

const HeaderContent = styled.div`
  text-align: center;
`

const Title = styled.h1`
  font-family: ${theme.fonts.itcGothicPro};
  font-size: ${theme.fontSize['2xl']};
  color: ${theme.colors.altovita};
  margin-bottom: 0.5rem;
`

const Subtitle = styled.p`
  font-size: ${theme.fontSize.base};
  font-weight: ${theme.fontWeight.light};
  color: ${theme.colors.greyText};
  line-height: normal;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;

  .embla {
    overflow: hidden;
  }

  .embla__container {
    display: flex;
    transition: height 0.2s;
    align-items: flex-start;
    grid-column-gap: 20px;
  }

  .embla__slide {
    flex: 0 0 100%;
    min-width: 0;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const StickyFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  max-width: inherit;
  width: 100%;
`

export const ShortlistApp = () => {
  const {
    approvedProperties,
    emblaRef,
    error,
    hasError: hadErrorLoadingHor,
    hor,
    isLoading,
    methods,
    nProperties,
    onApprovalToggle,
    onPropertyIndexChange,
    properties,
    selectedPropertyIndex,
    wasShortlistSent,
    isUpdatingApprovals,
    isSendToEmployeeModalOpen,
    onSubmitSendToEmployeeModal,
    onCloseSendToEmployeeModal,
    onClickSendToEmployeeButton,
    isSendingMail,
    mailError,
    onGoBackFromSuccessfulEmail,
    co2Data,
    dictionary,
  } = useGetShortlist()

  let content = null
  if (wasShortlistSent) {
    content = <SuccessfulEmployeeEmail onGoBack={onGoBackFromSuccessfulEmail} />
  } else if (isLoading) {
    content = (
      <SpinnerContainer>
        <Spinner size={32} color={theme.colors.primaryDefault} />
      </SpinnerContainer>
    )
  } else {
    content = (
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {(properties || []).map((property) => (
            <div key={property.id} className="embla__slide">
              <ShortlistProperty
                property={property}
                isMutationOnFlight={isUpdatingApprovals}
                isApproved={approvedProperties.includes(property.id)}
                onApprovalToggle={onApprovalToggle}
                hor={hor}
                co2Data={co2Data?.[property.id]}
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <SendToEmployeeModal
        isOpen={isSendToEmployeeModalOpen}
        onClose={onCloseSendToEmployeeModal}
        onSubmit={onSubmitSendToEmployeeModal}
        isLoading={isSendingMail}
        mailError={mailError}
        hor={hor}
      />
      <Container>
        <FormProvider {...methods}>
          <Header>
            <LogoContainer>
              <Logo
                alt="AltoVita"
                src="../../assets/logo-filled.png"
                title="AltoVita"
              />
            </LogoContainer>
            <HeaderContent>
              <Title>Shortlist</Title>
              <Subtitle>
                Review and approve options to share with the{' '}
                {dictionary.employee?.toLowerCase()}
              </Subtitle>
            </HeaderContent>
          </Header>

          {hadErrorLoadingHor && (
            <Message type="error">
              Error loading shortlist: {JSON.stringify(error)}
            </Message>
          )}
          <Content>
            {content}
            <SignOutButton />
          </Content>
          {!wasShortlistSent && (
            <StickyFooter>
              <ShortlistFooter
                selectedShortlistIndex={selectedPropertyIndex}
                onIndexChange={onPropertyIndexChange}
                onSendToEmployee={onClickSendToEmployeeButton}
                nSelectedShortlists={
                  methods.getValues('approvedShortlists')?.length || 0
                }
                nProperties={nProperties}
                canSendToEmployee={!isLoading && approvedProperties?.length > 0}
              />
            </StickyFooter>
          )}
        </FormProvider>
      </Container>
    </>
  )
}
